import { useState, useRef, useEffect } from 'react';
import { Elements, ElementsConsumer } from '@stripe/react-stripe-js';
import { loadStripe, StripeElements } from '@stripe/stripe-js';
import { safeLocale } from '@/utils/prices';
import { getStripeAppearance } from '@/utils/config';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@/components/commons/themeProvider';
import { Dialog } from '@/components/ui/dialog';
import PaymentDialogContent from './content';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

interface PaymentDialogProps {
  clientSecret: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

// Wrap Stripe Elements around the PaymentDialogContent (to be able to call useStripe())
export default function PaymentDialog({ clientSecret, isOpen, onOpenChange }: PaymentDialogProps) {
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const locale = searchParams.get('locale');

  const elementsRef = useRef<StripeElements | null>(null);
  const [stripeAppearance, setStripeAppearance] = useState(getStripeAppearance());

  useEffect(() => {
    // If no timeout, the appearance is not applied correctly
    setTimeout(() => {
      setStripeAppearance(getStripeAppearance());
    }, 0);
  }, [theme]);

  return (
    isOpen &&
    clientSecret && (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            locale: safeLocale(locale || navigator.languages[0]),
            appearance: stripeAppearance,
            fonts: [
              {
                cssSrc: 'https://fonts.googleapis.com/css?family=Inter:400,500',
              },
            ],
          }}
        >
          <ElementsConsumer>
            {({ elements }) => {
              elementsRef.current = elements;
              return (
                <>
                  <PaymentDialogContent isOpen={isOpen} onOpenChange={onOpenChange} />
                </>
              );
            }}
          </ElementsConsumer>
        </Elements>
      </Dialog>
    )
  );
}
